/**
 * Formatação para o usuário. Tudo em português; os identificadores, em inglês.
 *
 * Existe num arquivo só porque o mesmo número aparece na tabela, no mapa, no
 * detalhe e no gráfico — e "30,5 km/h" em um lugar e "30.5 kmh" em outro é o
 * tipo de inconsistência que faz o usuário duvidar do dado inteiro.
 */

const NUMBER = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });
const INTEGER = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });
const DATE_TIME = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "medium" });
const TIME = new Intl.DateTimeFormat("pt-BR", { timeStyle: "short" });

const EMPTY = "—";

export function formatSpeed(kmh: number | null | undefined): string {
  return kmh == null ? EMPTY : `${NUMBER.format(kmh)} km/h`;
}

export function formatCoordinate(value: number | null | undefined): string {
  return value == null ? EMPTY : value.toFixed(6);
}

export function formatVoltage(millivolts: number | null | undefined): string {
  return millivolts == null ? EMPTY : `${NUMBER.format(millivolts / 1000)} V`;
}

export function formatDistance(meters: number | string | null | undefined): string {
  if (meters == null) return EMPTY;
  const value = typeof meters === "string" ? Number(meters) : meters;
  if (!Number.isFinite(value)) return EMPTY;
  return value >= 1000 ? `${NUMBER.format(value / 1000)} km` : `${INTEGER.format(value)} m`;
}


export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? EMPTY : DATE_TIME.format(date);
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? EMPTY : TIME.format(date);
}

/** "há 3 min", "há 2 h". Para "quando foi o último pacote". */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return formatDuration(Math.round((Date.now() - date.getTime()) / 1000), { prefix: "há " });
}

/**
 * Duração legível a partir de segundos. Usada tanto no atraso quanto no "último
 * pacote", que é o motivo de ser uma função e não dois trechos parecidos.
 */
export function formatDuration(seconds: number | null | undefined, options: { prefix?: string } = {}): string {
  if (seconds == null || !Number.isFinite(seconds)) return EMPTY;
  const prefix = options.prefix ?? "";
  const absolute = Math.abs(Math.round(seconds));
  if (absolute < 60) return `${prefix}${absolute} s`;
  if (absolute < 3600) return `${prefix}${Math.round(absolute / 60)} min`;
  if (absolute < 86_400) return `${prefix}${NUMBER.format(absolute / 3600)} h`;
  return `${prefix}${NUMBER.format(absolute / 86_400)} d`;
}

/**
 * Horas decimais em `hh:mm` — 7,4 h vira `07:24`.
 *
 * O horímetro do dia é lido contra a escala ("saiu às 5, deveria ter 9 h"), e
 * "7,4 h" obriga a uma multiplicação de cabeça em cada leitura para saber se
 * são 24 ou 40 minutos. Os minutos são ARREDONDADOS, não truncados: 0,999 h é
 * `01:00`, e não `00:59`.
 */
export function formatHoursAsClock(hours: number | null | undefined): string {
  if (hours == null || !Number.isFinite(hours) || hours < 0) return EMPTY;
  const minutes = Math.round(hours * 60);
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/** Nível de combustível: vem em DÉCIMOS de porcento (1000 = 100%). */
export function formatFuelLevel(decipercent: number | null | undefined): string {
  return decipercent == null ? EMPTY : `${NUMBER.format(decipercent / 10)}%`;
}

/** Percentual guardado em DÉCIMOS de porcento (1000 = 100%). */
export function formatDecipercent(decipercent: number | null | undefined): string {
  return decipercent == null ? EMPTY : `${NUMBER.format(decipercent / 10)}%`;
}

export function formatRpm(rpm: number | null | undefined): string {
  return rpm == null ? EMPTY : `${INTEGER.format(rpm)} rpm`;
}

export function formatCelsius(celsius: number | null | undefined): string {
  return celsius == null ? EMPTY : `${INTEGER.format(celsius)} °C`;
}

/** Rumo em bússola: mais útil que "187°" para quem olha rápido. */
export function formatHeading(degrees: number | null | undefined): string {
  if (degrees == null) return EMPTY;
  const points = ["N", "NE", "L", "SE", "S", "SO", "O", "NO"];
  const index = Math.round(((degrees % 360) + 360) % 360 / 45) % 8;
  return `${INTEGER.format(degrees)}° ${points[index]}`;
}
